import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Tooltip, Typography } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import useFeatureFlags from 'api/useFeatureFlags';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import { AxiosError } from 'axios';
import { ETaskId } from 'common/contracts';
import { EFeatureFlag } from 'constants/enums';
import { Decimal } from 'decimal.js';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';

import AcTooltip from 'components/AcTooltip/AcTooltip';
import EmptyState from 'components/EmptyState/EmptyState';
import FormHeader from 'components/FormHeader/FormHeader';
import { Flex } from 'components/ui/flex';

import usePricing from '../../api/usePricing';
import { MAX_USD_PRICE, MIN_USD_PRICE } from '../../constants/constants';
import { errorResponse } from '../../utils/errorsTextHelper';
import { permissionsUtil } from '../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../utils/showToast';
import DataTable from '../DataTableComponent/DataTable';
import Input from '../Input/Input';

import { AVAILABLE_COUNTRIES } from './Pricing.constants';
import { CustomPricing, PricingMeta } from './Pricing.types';
import PricingTableInputCell from './PricingTableInputCell';

enum TAX_TYPE {
  TAX_INCLUDED = 'taxIncluded',
  TAX_EXCLUDED = 'taxExcluded'
}

interface ExtendedCustomPricing extends CustomPricing {
  country?: string;
  taxModel?: string;
  taxRate?: number;
  customPricingDeviation?: string;
  rate?: number;
}

const PricingForm = () => {
  const isManuallyUpdatedListFilled = useRef(false);
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { pricePointId } = useParams();

  const { featureFlags } = useFeatureFlags({ publisherId });
  const { userDetails } = useUserDetails();
  const { canUserEdit } = usePermissions();
  const { setTaskStatus, hasCategories, isWebStore, isWebCheckout } =
    useChecklistState();

  const {
    getPricePoint,
    getPricings,
    calculatePricing,
    updatePricing,
    addPricing
  } = usePricing(publisherId, pricePointId, true);

  const [currentPricePoint, setCurrentPricePoint] = useState<Decimal>(
    new Decimal(MIN_USD_PRICE)
  );
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isPricePointTaken, setIsPricePointTaken] = useState(false);
  const [prices, setPrices] = useState<CustomPricing[]>([]);
  const manuallyUpdatedPricesByCountryCode3 = useMemo(() => {
    if (!prices.length) return [];
    isManuallyUpdatedListFilled.current = true;
    return prices
      ?.filter((price: CustomPricing) => price.manuallyUpdated)
      .map((p: CustomPricing) => p.countryCode3);
  }, [prices]);
  const [isDirty, setIsDirty] = useState(false);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 1000;
  const [isTyping, setIsTyping] = useState(false);
  const isLoading = useMemo(() => {
    return getPricePoint.isFetching || calculatePricing.isLoading || isTyping;
  }, [getPricePoint.isFetching, calculatePricing.isLoading, isTyping]);

  const hasFeatureFlagPricePointAlerting =
    featureFlags?.[EFeatureFlag.DASHBOARD_PRICE_POINT_ALERTING];

  const pricePointDescription = useMemo(() => {
    if (currentPricePoint?.lt(MIN_USD_PRICE)) {
      return `Price point can't be lower than $${MIN_USD_PRICE}`;
    }

    if (currentPricePoint?.gt(MAX_USD_PRICE)) {
      return `Price point can't be higher than $${MAX_USD_PRICE}`;
    }

    if (isPricePointTaken && !pricePointId) {
      return 'There is already a price point with this value';
    }

    return '';
  }, [currentPricePoint, isPricePointTaken, pricePointId]);

  const checkoutIsPricePointTaken = () => {
    return getPricings.data?.result.find((pricePoint: PricingMeta) =>
      new Decimal(pricePoint.priceInCents).eq(currentPricePoint.mul(100))
    );
  };

  const handlePricePointChange = () => {
    if (checkoutIsPricePointTaken()) {
      setIsPricePointTaken(true);
      return;
    }

    if (!isManuallyUpdatedListFilled.current && pricePointId) return;
    setIsPricePointTaken(false);
    const anchors = [currentPricePoint.mul(100).toNumber()];

    calculatePricing.mutate(anchors, {
      onSuccess: (data: any) => {
        const newPrices = data.result[0].customPricing.map(
          (price: CustomPricing) => {
            if (
              manuallyUpdatedPricesByCountryCode3.includes(price.countryCode3)
            ) {
              return prices.find(
                (p: CustomPricing) => price.countryCode3 === p.countryCode3
              );
            } else return price;
          }
        );
        setPrices(newPrices);
        if (hasCategories && (isWebStore || isWebCheckout)) {
          setTaskStatus(ETaskId.CREATE_PRICE_POINTS);
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          showToast({
            message: errorResponse(error),
            status: EToasterStatus.ERROR
          });
        }
      }
    });
  };

  const updateCountryPriceManually = (
    newPrice: Decimal,
    countryCode3: string
  ) => {
    const newPrices = [...prices];

    for (let i = 0; i < newPrices.length; i++) {
      if (newPrices[i].countryCode3 === countryCode3) {
        newPrices[i].price = newPrice;
        newPrices[i].manuallyUpdated = true;
        newPrices[i].fresh = false;
        break;
      }
    }
    setIsDirty(true);
    setPrices(newPrices);
  };

  const getFormattedCurrency = (params: any, getBasePrice = false): number => {
    const multiplier = params.row.multiplier;
    const customPrice = new Decimal(params.row.price)
      .div(multiplier)
      .toNumber();

    // find the rate of the currency
    let rate = params.row.rate || 1;
    rate = new Decimal(rate).mul(100).div(multiplier).toNumber();

    const priceFromPoint = currentPricePoint.mul(rate).toNumber();

    return getBasePrice || !customPrice
      ? new Decimal(params.row.price).div(multiplier).toNumber()
      : customPrice || priceFromPoint;
  };

  const formatPrices = () => {
    return prices.map((price: CustomPricing) => {
      return {
        countryCode3: price.countryCode3.toUpperCase(),
        currencyCode: price.currencyCode,
        price: new Decimal(price.price),
        manuallyUpdated: price.manuallyUpdated || false
      } as CustomPricing;
    });
  };

  const handleAddPricing = () => {
    const formattedPrices = formatPrices();
    addPricing.mutate({
      priceInCents: currentPricePoint.mul(100),
      customPricing: formattedPrices
    });
    navigate('../pricing/points');
  };

  const handleEditPricing = () => {
    if (!pricePointId) return;
    const formattedPrices = formatPrices();
    const pricingObject = {
      priceInCents: currentPricePoint.mul(100),
      customPricing: formattedPrices,
      _id: pricePointId
    };
    updatePricing.mutate(pricingObject);
    navigate('../pricing/points');
  };

  useEffect(() => {
    if (!pricePointId) {
      setCurrentPricePoint(new Decimal(MIN_USD_PRICE));
    } else if (!getPricePoint.isLoading) {
      getPricePoint.refetch();
    }
  }, [pricePointId]);

  useEffect(() => {
    if (!getPricePoint.data) return;
    setCurrentPricePoint(
      new Decimal(getPricePoint.data.result.priceInCents).div(100)
    );
    setPrices(getPricePoint.data.result.customPricing);
  }, [getPricePoint.data]);

  useEffect(() => {
    if (
      currentPricePoint &&
      currentPricePoint.greaterThanOrEqualTo(new Decimal(MIN_USD_PRICE))
    ) {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      setIsTyping(true);

      debounceTimeout.current = setTimeout(() => {
        handlePricePointChange();
        setIsTyping(false);
      }, DEBOUNCE_DELAY);

      return () => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      };
    }
  }, [currentPricePoint]);

  return (
    <div className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll">
      <FormHeader
        title={pricePointId ? 'Edit Pricing' : 'New Pricing'}
        mainButtonText="Save"
        onCloseForm={() => navigate('../pricing/points')}
        onMainButtonClick={() =>
          pricePointId ? handleEditPricing() : handleAddPricing()
        }
        isFormValid={
          isDirty &&
          canUserEdit &&
          !isTyping &&
          !currentPricePoint?.gt(MAX_USD_PRICE) &&
          !currentPricePoint?.lt(MIN_USD_PRICE) &&
          !getPricePoint.isFetching &&
          !calculatePricing.isLoading &&
          (!pricePointId ? !isPricePointTaken : true)
        }
      />
      <Flex className="gap-4 px-[148px] py-[39px]">
        <Typography fontWeight={600}>USD Price Point</Typography>
        <Flex className="gap-4">
          <Input
            dataTestId="mainPricingInpt"
            className="pl-1 w-[373px]"
            value={
              isEditingPrice
                ? currentPricePoint.toString()
                : currentPricePoint.toFixed(2)
            }
            type="number"
            onChange={(e) => {
              setIsDirty(true);
              setCurrentPricePoint(new Decimal(e.target.value || 0));
            }}
            onFocus={() => setIsEditingPrice(true)}
            onBlur={() => setIsEditingPrice(false)}
            min={MIN_USD_PRICE}
            max={MAX_USD_PRICE}
            leftIcon={<span>$</span>}
            leftIconStyles="text-base-foreground text-p-s pl-1 h-full flex items-center"
            state={
              (isPricePointTaken && !pricePointId) ||
                currentPricePoint?.lt(MIN_USD_PRICE) ||
                currentPricePoint?.gt(MAX_USD_PRICE)
                ? 'error'
                : 'default'
            }
            description={pricePointDescription}
          />
        </Flex>

        {pricePointDescription?.length > 0 ||
          (isPricePointTaken && !pricePointId) ? (
          <EmptyState
            title="Add a price point"
            description={
              <>
                <Typography variant="body2" color="textSecondary">
                  Price points shape how customers experience your product.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Set the price at which your product or service will be offered
                </Typography>
              </>
            }
            icon="fa-regular fa-circle-dollar"
            hideButton
          />
        ) : (
          <DataTable<ExtendedCustomPricing>
            bordered
            enableColumnResizing
            rowIdIdentifier="countryCode3"
            getRowId={(row) => row.countryCode3}
            columns={
              [
                {
                  id: 'country',
                  accessorKey: 'country',
                  header: 'Country',
                  enableSorting: true,
                  cell: ({ row, getValue }) => {
                    return (
                      <Flex
                        direction="row"
                        justify="start"
                        align="center"
                        className="gap-4"
                      >
                        <img
                          src={`./assets/flags/${(
                            AVAILABLE_COUNTRIES.find(
                              (country) => country._id === row.id.toLowerCase()
                            )?.twoLetterId || ''
                          ).toLowerCase()}.png`}
                          width={28}
                          height={20}
                          alt=""
                        />
                        <Tooltip
                          arrow
                          title={
                            <AcTooltip content={String(getValue()) || ''} />
                          }
                        >
                          <div
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {String(getValue())}
                          </div>
                        </Tooltip>
                      </Flex>
                    );
                  }
                },
                {
                  id: 'currencyCode',
                  accessorKey: 'currencyCode',
                  header: 'Currency',
                  enableSorting: true,
                  cell: ({ getValue }) => {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        {String(getValue())}
                      </Typography>
                    );
                  }
                },
                {
                  id: 'price',
                  accessorKey: 'price',
                  header: 'Price',
                  enableSorting: true,
                  cell: ({ row }) => {
                    return (
                      <PricingTableInputCell
                        dataTestId={`priceInput-${row.original.countryCode3.toLowerCase()}`}
                        onPriceUpdate={updateCountryPriceManually}
                        pricePoint={getFormattedCurrency({ row: row.original })}
                        customPriceData={row.original}
                      />
                    );
                  },
                  sortingFn: (rowA, rowB) => {
                    const getPrice = (countryCode: string) => {
                      const countryPricing = prices.find(
                        (cp: CustomPricing) =>
                          cp.countryCode3 === countryCode.toUpperCase()
                      );
                      if (!countryPricing) return 0;
                      const rawPrice = parseFloat(
                        countryPricing.price.toString().replace(/[^0-9.]/g, '')
                      );
                      const multiplier = countryPricing.multiplier || 100;
                      return multiplier === 1
                        ? rawPrice
                        : rawPrice / multiplier;
                    };

                    const priceA = getPrice(String(rowA.id));
                    const priceB = getPrice(String(rowB.id));

                    return priceA - priceB;
                  }
                },
                {
                  id: 'taxModel',
                  accessorKey: 'taxModel',
                  header: 'Tax model',
                  enableSorting: true,
                  cell: ({ row }) => {
                    return row.original.taxModel === TAX_TYPE.TAX_INCLUDED
                      ? 'Included'
                      : 'Excluded';
                  }
                },
                {
                  id: 'taxRate',
                  accessorKey: 'taxRate',
                  header: 'Tax rate',
                  enableSorting: true,
                  cell: ({ row }) => {
                    return (row.original.taxRate ?? 0) / 100 + '%';
                  }
                },
                ...(hasFeatureFlagPricePointAlerting
                  ? ([
                    {
                      id: 'customPricingDeviation',
                      accessorKey: 'customPricingDeviation',
                      header: () => (
                        <Tooltip
                          arrow
                          title={
                            <AcTooltip content="Currency exchange rate change since update" />
                          }
                        >
                          <span style={{ fontWeight: '600' }}>
                            Exchange Rate Drift
                          </span>
                        </Tooltip>
                      ),
                      enableSorting: true,
                      cell: ({ row }) => {
                        if (row.original.customPricingDeviation)
                          return (
                            <Flex
                              direction="row"
                              align="center"
                              className="gap-2"
                            >
                              {row.original.customPricingDeviation}
                              <Tooltip
                                arrow
                                title={
                                  <AcTooltip content="Exchange rate drift anomaly" />
                                }
                              >
                                <ErrorOutlineOutlinedIcon
                                  sx={{ color: '#A8A9B2' }}
                                />
                              </Tooltip>
                            </Flex>
                          );
                      }
                    }
                  ] as ColumnDef<ExtendedCustomPricing>[])
                  : []),
                {
                  id: 'status',
                  accessorKey: 'manuallyUpdated',
                  header: 'Status',
                  enableSorting: true,
                  cell: ({ row }) => {
                    const cp = prices.find(
                      (cp: CustomPricing) =>
                        cp.countryCode3 === row.id.toUpperCase()
                    );
                    return (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          color:
                            (cp && cp.manuallyUpdated) ||
                              row.original.manuallyUpdated
                              ? 'green'
                              : 'black'
                        }}
                      >
                        {(cp && cp.manuallyUpdated) ||
                          row.original.manuallyUpdated
                          ? 'Manually Updated'
                          : 'Default'}
                      </Typography>
                    );
                  },
                  sortingFn: (rowA, rowB) => {
                    const cpA = prices.find(
                      (cp: CustomPricing) =>
                        cp.countryCode3 === String(rowA.id).toUpperCase()
                    );
                    const cpB = prices.find(
                      (cp: CustomPricing) =>
                        cp.countryCode3 === String(rowB.id).toUpperCase()
                    );

                    const manuallyUpdatedA = cpA?.manuallyUpdated || false;
                    const manuallyUpdatedB = cpB?.manuallyUpdated || false;

                    if (manuallyUpdatedA && !manuallyUpdatedB) {
                      return -1;
                    }
                    if (!manuallyUpdatedA && manuallyUpdatedB) {
                      return 1;
                    }
                    return 0;
                  }
                }
              ] as ColumnDef<ExtendedCustomPricing>[]
            }
            data={prices.filter((row: any) => row.countryCode3 !== 'USA')}
            isLoading={isLoading}
            size="lg"
            showPagination={false}
            rowsPerPage={1000}
          />
        )}
      </Flex>
    </div>
  );
};

export default PricingForm;
